import { useMemo } from 'react';
import styles from './styles.module.scss';
import Button from '@components/common/button';
import Typography from '@components/common/typography';
import { useNavigationContext } from '@hooks/context/useNavigationContext';
import { Icon } from '@components/icons';
import Info from '@components/common/info';
import CircleDiagram from '@components/common/circle_diagram';
import illustrationDesktop from '@svg/illustrations/loyalty_banner_desktop.svg';
import illustrationResponsive from '@svg/illustrations/loyalty_banner_responsive.svg';
import useResponsive from '@hooks/helpers/useResponsive';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { apiAccountService } from '@api';
import { fixedNumbers, formatDate, getLoyaltyLevelString } from '@utils/index';
import { useUserContext } from '@hooks/context/useUserContext';
import { useLocales } from '@hooks/helpers/useLocales';
import { dateDifferenceInDays } from '@utils/date';
import { ELoyaltyLevel } from '@xeppt/xeppt-sdk/types/loyalty';
import LoyaltyGraphicDesktop from '@components/illustrations/loyalty_graphic_desktop';
import LoyaltyGraphicResponsive from '@components/illustrations/loyalty_graphic_responsive';
import { EFundsOperation } from '@enum';
import { EProductName } from '@xeppt/xeppt-sdk/types/products';
import { EUserType } from '@xeppt/xeppt-sdk/types';

const LoyaltyLayout = () => {
    const { handleBack } = useNavigationContext();
    const { windowWidth } = useResponsive();
    const { submitLocale } = useLocales();
    const { loyalty, products, user } = useUserContext();

    const { data: summary } = useApiQuery({
        method: () => apiAccountService.getMyLoyaltySummary(),
        isInitialRequest: false
    });

    const points_to_gold = useMemo(() => {
        return (summary?.loyalty_settings?.[`points_to_${ELoyaltyLevel.B}`] || 0) -
            (loyalty?.points || 0) <
            0
            ? 0
            : (summary?.loyalty_settings?.[`points_to_${ELoyaltyLevel.B}`] || 0) -
                  (loyalty?.points || 0);
    }, [loyalty, summary]);

    const points_to_platinum = useMemo(() => {
        return (summary?.loyalty_settings?.[`points_to_${ELoyaltyLevel.C}`] || 0) -
            (loyalty?.points || 0) <
            0
            ? 0
            : (summary?.loyalty_settings?.[`points_to_${ELoyaltyLevel.C}`] || 0) -
                  (loyalty?.points || 0);
    }, [loyalty, summary]);

    const percentToGold = useMemo(() => {
        return Math.min(
            ((loyalty?.points || 0) /
                (summary?.loyalty_settings?.[`points_to_${ELoyaltyLevel.B}`] || 0)) *
                100,
            100
        );
    }, [loyalty, summary]);

    const percentToPlatinum = useMemo(() => {
        return Math.min(
            ((loyalty?.points || 0) /
                (summary?.loyalty_settings?.[`points_to_${ELoyaltyLevel.C}`] || 0)) *
                100,
            100
        );
    }, [loyalty, summary]);

    const aPlanData = useMemo(() => {
        if (loyalty?.level === ELoyaltyLevel.A) {
            return loyalty?.current_plan;
        } else {
            return summary?.loyalty_settings?.plans?.[ELoyaltyLevel.A];
        }
    }, [loyalty, summary]);

    const bPlanData = useMemo(() => {
        if (loyalty?.level === ELoyaltyLevel.B) {
            return loyalty?.current_plan;
        } else {
            return summary?.loyalty_settings?.plans?.[ELoyaltyLevel.B];
        }
    }, [loyalty, summary]);

    const cPlanData = useMemo(() => {
        if (loyalty?.level === ELoyaltyLevel.C) {
            return loyalty?.current_plan;
        } else {
            return summary?.loyalty_settings?.plans?.[ELoyaltyLevel.C];
        }
    }, [loyalty, summary]);

    const data = useMemo(() => {
        const ignoredOperations = [
            EFundsOperation.EPS_DEBIT_DEPOSIT,
            EFundsOperation.CARD_DEBIT_DEPOSIT,
            EFundsOperation.E_TRANSFER_DEBIT_TRANSFER,
            EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD,
            EFundsOperation.GIFT_CARD_DEBIT_RESERVE,
            EFundsOperation.GIFT_CARD_CREDIT_RESERVE
            // EFundsOperation.WALLET_CREDIT_CARD_SERVICE
        ];
        const sourcePoints = summary?.operation_points;
        const currentPlan = loyalty?.current_plan;
        const operations = Object.values(EFundsOperation).filter(
            (item) => !ignoredOperations.includes(item)
        );
        const operationNames = {
            [EFundsOperation.WALLET_CREDIT_TRANSFER]: 'Send money via XEPPT',
            [EFundsOperation.WALLET_DEBIT_TRANSFER]: 'Receive money via XEPPT',
            [EFundsOperation.WALLET_CREDIT_REQUEST]: 'Approve someone’s money request',
            [EFundsOperation.WALLET_DEBIT_REQUEST]: 'Request money via XEPPT (when approved)',
            [EFundsOperation.WALLET_CREDIT_CARD_DEPOSIT]: 'Load XEPPT card balance',
            [EFundsOperation.WALLET_CREDIT_CARD_SERVICE]: 'Order XEPPT card',
            [EFundsOperation.PAY_BILL_CREDIT_PAYMENT]: 'Pay a bill',
            [EFundsOperation.E_TRANSFER_CREDIT_TRANSFER]:
                'Send money via <i>Interac</i> e-Transfer®',
            // [EFundsOperation.E_TRANSFER_DEBIT_TRANSFER]:
            //     'Receive money via <i>Interac</i> e-Transfer®',
            [EFundsOperation.E_TRANSFER_DEBIT_REQUEST]:
                'Request money via <i>Interac</i> e-Transfer® (when approved)',
            [EFundsOperation.EFT_CREDIT_WITHDRAWAL]: 'Withdraw to linked bank account',
            [EFundsOperation.EFT_DEBIT_DEPOSIT]: 'Load balance from linked bank account',
            [EFundsOperation.EPS_DEBIT_DEPOSIT]: 'Load balance from linked card',
            [EFundsOperation.CARD_CREDIT_WITHDRAWAL]: 'Pay with XEPPT card',
            [EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD]: 'Issue XEPPT gift card',
            [EFundsOperation.GIFT_CARD_DEBIT_GIFT_CARD_REDEEM]: 'Redeem XEPPT gift card'
        };
        return operations.map((item) => {
            return {
                source: sourcePoints?.[item] || 0,
                currentPlan: currentPlan?.[item],
                aPlan: aPlanData?.[item],
                bPlan: bPlanData?.[item],
                cPlan: cPlanData?.[item],
                //@ts-ignore
                name: operationNames[item as EFundsOperation],
                operation: item
            };
        });
    }, [loyalty, summary]);

    const productsData = useMemo(() => {
        const productsName = {
            [EProductName.OrderCardReplacement]: 'Replace XEPPT prepaid card',
            [EProductName.OrderPhysicCardFirstTime]: 'Request XEPPT primary card first order',
            [EProductName.OrderPhysicCardNextTimes]: 'Request XEPPT primary card',
            [EProductName.OrderSupplementaryPhysicCardFirstTime]:
                'Request XEPPT supplementary card first order',
            [EProductName.OrderSupplementaryPhysicCardNextTimes]:
                'Request XEPPT supplementary card',
            [EProductName.OrderGiftCard]: 'Issue XEPPT gift card'
        };
        return Object.keys(productsName)
            .map((item) => {
                if (user?.type === EUserType.INDIVIDUAL && item !== EProductName.OrderGiftCard)
                    return undefined;
                return {
                    currentPlan: products?.find(
                        (product) => product.level === ELoyaltyLevel.A && product.name === item
                    ),
                    aPlan: products?.find(
                        (product) => product.level === ELoyaltyLevel.A && product.name === item
                    ),
                    bPlan: products?.find(
                        (product) => product.level === ELoyaltyLevel.B && product.name === item
                    ),
                    cPlan: products?.find(
                        (product) => product.level === ELoyaltyLevel.C && product.name === item
                    ),
                    //@ts-ignore
                    name: productsName[item as EProductName]
                };
            })
            .filter((item) => item !== undefined);
    }, [products, loyalty]);

    console.log({ productsData });

    return (
        <div className={styles.wrapper}>
            <div className={styles.header}>
                <Button
                    className={styles.back}
                    leftIcon="arrow_left"
                    variant="text-gray"
                    onClick={handleBack}>
                    {submitLocale('back')}
                </Button>
                <Typography variant="h4">My Points</Typography>
            </div>
            <div className={styles.main_info_wrapper}>
                <div className={styles.item}>
                    <div className={styles.icon}>
                        <Icon name="person" />
                    </div>
                    <div className={styles.text_wrapper}>
                        <Typography variant="body3">
                            Current profile level{' '}
                            <Info
                                variant="top"
                                content="Your current Momentum tier based on your points earned in the previous period"
                            />
                        </Typography>
                        <Typography variant="h4">
                            {getLoyaltyLevelString(loyalty?.level)}
                        </Typography>
                    </div>
                </div>
                <div className={styles.item}>
                    <div className={styles.icon}>
                        <Icon name="coins" />
                    </div>
                    <div className={styles.text_wrapper}>
                        <Typography variant="body3">
                            Points balance{' '}
                            <Info
                                variant="top"
                                content="Number of points you’ve earned so far in the current period"
                            />
                        </Typography>
                        <Typography variant="h4">{fixedNumbers(loyalty?.points)}</Typography>
                    </div>
                </div>
                <div className={styles.item}>
                    <div className={styles.icon}>
                        <Icon name="calendar" />
                    </div>
                    <div className={styles.text_wrapper}>
                        <Typography variant="body3">
                            Days until period ends{' '}
                            <Info
                                variant="top"
                                content="Number of days remaining in your current Momentum earning period"
                            />
                        </Typography>
                        <Typography variant="h4">
                            {loyalty?.next_review_date
                                ? dateDifferenceInDays(new Date(), loyalty?.next_review_date)
                                : 0}
                        </Typography>
                    </div>
                </div>
                <div className={styles.item}>
                    <div className={styles.icon}>
                        <Icon name="history" />
                    </div>
                    <div className={styles.text_wrapper}>
                        <Typography variant="body3">
                            Total points earned{' '}
                            <Info
                                variant="top"
                                content="Total number of points you've earned since joining XEPPT"
                            />
                        </Typography>
                        <Typography variant="h4">{fixedNumbers(summary?.points)}</Typography>
                    </div>
                </div>
            </div>
            <div className={styles.data_wrapper}>
                <div className={`${styles.content_block} ${styles.diagrams_wrapper}`}>
                    <Typography variant="h5" className={styles.title}>
                        Progress by profile levels
                    </Typography>
                    <div className={styles.diagrams}>
                        <CircleDiagram progress={percentToGold}>
                            <Typography variant="h3" alignment="center">
                                {fixedNumbers(points_to_gold)}
                            </Typography>
                            <Typography variant="body4" alignment="center">
                                {loyalty?.level !== ELoyaltyLevel.B
                                    ? 'Point remaining to'
                                    : 'Points needed to maintain'}{' '}
                                <b>Gold</b>
                            </Typography>
                        </CircleDiagram>
                        <CircleDiagram progress={percentToPlatinum}>
                            <Typography variant="h3" alignment="center">
                                {fixedNumbers(points_to_platinum)}
                            </Typography>
                            <Typography variant="body4" alignment="center">
                                {loyalty?.level !== ELoyaltyLevel.C
                                    ? 'Point remaining to'
                                    : 'Points needed to maintain'}{' '}
                                <b>Platinum</b>
                            </Typography>
                        </CircleDiagram>
                    </div>
                    {loyalty?.next_review_date && (
                        <Typography variant="body4" className={styles.diagrams_footer}>
                            Next profile level review: <br />{' '}
                            <b>{formatDate(loyalty?.next_review_date, 'DD MMMM YYYY')}</b>
                        </Typography>
                    )}
                </div>
                <div className={styles.content_block}>
                    <Typography variant="h5" className={styles.title}>
                        Source of points
                    </Typography>
                    <div className={styles.details_table}>
                        <div className={styles.row}>
                            <Typography variant="body4" weight="semibold">
                                Source
                            </Typography>
                            <Typography variant="body4" weight="semibold">
                                Points
                            </Typography>
                        </div>
                        {data.map((item) => {
                            if (!item?.name) return null;
                            return (
                                <div className={styles.row} key={item.name}>
                                    <Typography
                                        variant="body4"
                                        dangerouslySetInnerHTML={{ __html: item.name }}
                                    />
                                    <Typography variant="body4">
                                        {fixedNumbers(item?.source)}
                                    </Typography>
                                </div>
                            );
                        })}
                        <div className={styles.row}>
                            <Typography variant="body4" weight="semibold">
                                TOTAL
                            </Typography>
                            <Typography variant="body4" weight="semibold">
                                {fixedNumbers(summary?.points)}
                            </Typography>
                        </div>
                    </div>
                </div>
                <div className={styles.banner}>
                    <div>
                        <Typography variant="h5" alignment="center" className={styles.title}>
                            Momentum is building
                        </Typography>
                        <Typography
                            variant="body4"
                            alignment="center"
                            className={styles.description}>
                            Find out how to make it work for you
                        </Typography>
                    </div>
                    <Button
                        variant="gold"
                        size="small"
                        onClick={() => {
                            window.open('https://xeppt.com/momentum', '_blank');
                        }}>
                        {submitLocale('learn_more')}
                    </Button>
                    {windowWidth > 1200 ? (
                        <img src={illustrationDesktop} alt="" />
                    ) : (
                        <img src={illustrationResponsive} alt="" />
                    )}
                </div>
            </div>
            <div className={styles.info_wrapper}>
                <div className={styles.content_block}>
                    <Typography variant="h5" className={styles.title}>
                        How to upgrade
                    </Typography>
                    {windowWidth > 1200 || windowWidth <= 950 ? (
                        <LoyaltyGraphicDesktop
                            goldPoints={
                                summary?.loyalty_settings?.[`points_to_${ELoyaltyLevel.B}`] || 0
                            }
                            platinumPoints={
                                summary?.loyalty_settings?.[`points_to_${ELoyaltyLevel.C}`] || 0
                            }
                        />
                    ) : (
                        <LoyaltyGraphicResponsive
                            goldPoints={
                                summary?.loyalty_settings?.[`points_to_${ELoyaltyLevel.B}`] || 0
                            }
                            platinumPoints={
                                summary?.loyalty_settings?.[`points_to_${ELoyaltyLevel.C}`] || 0
                            }
                        />
                    )}
                </div>
                <div className={styles.content_block}>
                    <Typography variant="h5" className={styles.title}>
                        How to earn points
                    </Typography>
                    <div className={styles.details_table}>
                        <div className={styles.row}>
                            <Typography variant="body4" weight="semibold">
                                Transaction type
                            </Typography>
                            <Typography variant="body4" weight="semibold">
                                Points earned
                            </Typography>
                        </div>
                        {data.map((item) => {
                            if (
                                ((item.currentPlan?.points || 0) <= 0 &&
                                    (item.currentPlan?.amount || 0) <= 0) ||
                                item.operation === EFundsOperation.WALLET_CREDIT_CARD_SERVICE
                            )
                                return null;
                            return (
                                <div className={styles.row} key={item.name}>
                                    <Typography
                                        variant="body4"
                                        dangerouslySetInnerHTML={{
                                            __html: item.name
                                        }}
                                    />
                                    <Typography variant="body4">
                                        +{item.currentPlan?.points || 0} pts / $
                                        {item.currentPlan?.amount?.toFixed(2) || '0.00'}
                                    </Typography>
                                </div>
                            );
                        })}
                        {productsData.map((item) => {
                            if (
                                !item ||
                                ((item.currentPlan?.points || 0) <= 0 &&
                                    (item.currentPlan?.price || 0) <= 0)
                            )
                                return null;
                            return (
                                <div className={styles.row} key={item.name}>
                                    <Typography variant="body4">{item.name}</Typography>
                                    <Typography variant="body4">
                                        +{item.currentPlan?.points || 0} pts / $
                                        {item.currentPlan?.price?.toFixed(2) || '0.00'}
                                    </Typography>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            <div className={`${styles.content_block} ${styles.scroll_table}`}>
                <Typography variant="h5" className={styles.title}>
                    Enjoy lower fees as you build Momentum
                </Typography>
                {summary && (
                    <table className={styles.info_table}>
                        <thead>
                            <tr>
                                <td>
                                    <Typography variant="body4" weight="semibold">
                                        Transaction type
                                    </Typography>
                                </td>
                                <td>
                                    <Typography variant="body4" weight="semibold">
                                        Fees on Silver level, CAD
                                    </Typography>
                                </td>
                                <td>
                                    <Typography variant="body4" weight="semibold">
                                        Fees on Gold level, CAD
                                    </Typography>
                                </td>
                                <td>
                                    <Typography variant="body4" weight="semibold">
                                        Fees on Platinum level, CAD
                                    </Typography>
                                </td>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((item) => {
                                if (
                                    (item.aPlan?.percent === 0 &&
                                        item.bPlan?.percent === 0 &&
                                        item.cPlan?.percent === 0 &&
                                        item.aPlan.flat === 0 &&
                                        item.bPlan?.flat === 0 &&
                                        item.cPlan?.flat === 0) ||
                                    item.operation === EFundsOperation.WALLET_CREDIT_CARD_SERVICE
                                )
                                    return null;
                                return (
                                    <tr key={item.name}>
                                        <td>
                                            <Typography
                                                variant="body4"
                                                dangerouslySetInnerHTML={{ __html: item.name }}
                                            />{' '}
                                        </td>
                                        <td>
                                            <Typography variant="body4">
                                                {item.aPlan?.percent !== 0
                                                    ? `${item.aPlan?.percent?.toFixed(2)}%`
                                                    : '0.00%'}
                                                {item.aPlan?.flat !== 0
                                                    ? `${item.aPlan?.percent !== 0 ? ' + ' : ''}$${item.aPlan?.flat?.toFixed(2)}`
                                                    : '$0.00'}
                                            </Typography>
                                        </td>
                                        <td>
                                            <Typography variant="body4">
                                                {item.bPlan?.percent !== 0
                                                    ? `${item.bPlan?.percent?.toFixed(2)}%`
                                                    : '0.00%'}
                                                {item.bPlan?.flat !== 0
                                                    ? `${item.bPlan?.percent !== 0 ? ' + ' : ''}$${item.bPlan?.flat?.toFixed(2)}`
                                                    : '$0.00'}
                                            </Typography>
                                        </td>
                                        <td>
                                            <Typography variant="body4">
                                                {item.cPlan?.percent !== 0
                                                    ? `${item.cPlan?.percent?.toFixed(2)}%`
                                                    : '0.00%'}
                                                {item.cPlan?.flat !== 0
                                                    ? `${item.cPlan?.percent !== 0 ? ' + ' : ''}$${item.cPlan?.flat?.toFixed(2)}`
                                                    : '$0.00'}
                                            </Typography>
                                        </td>
                                    </tr>
                                );
                            })}
                            {productsData.map((item) => {
                                if (!item) return null;
                                return (
                                    <tr key={item.name}>
                                        <td>
                                            <Typography variant="body4">
                                                {item.name}
                                            </Typography>{' '}
                                        </td>
                                        <td>
                                            <Typography variant="body4">
                                                ${item.aPlan?.price?.toFixed(2)}
                                            </Typography>
                                        </td>
                                        <td>
                                            <Typography variant="body4">
                                                ${item.bPlan?.price?.toFixed(2)}
                                            </Typography>
                                        </td>
                                        <td>
                                            <Typography variant="body4">
                                                ${item.cPlan?.price?.toFixed(2)}
                                            </Typography>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default LoyaltyLayout;
